import { getLenis } from '../lib/lenis';
import { getEmbed, renderFrame } from '../lib/embed';

/**
 * Video modal — opens a native <dialog> with an embedded Vimeo/YouTube/HTML5
 * player when any element with `[data-video-url]` is clicked.
 *
 * The single dialog instance lives in the page footer. We rewrite its inner
 * frame each time it's opened, and tear the frame down on close so playback
 * actually stops (otherwise iframe audio can keep going).
 */

export function initVideoModal(): void {
  const modal = document.querySelector<HTMLDialogElement>('[data-video-modal]');
  if (!modal) return;

  const frame = modal.querySelector<HTMLElement>('[data-video-frame]');
  const closeBtn = modal.querySelector<HTMLButtonElement>('[data-video-close]');
  if (!frame || !closeBtn) return;

  const open = (url: string): void => {
    const embed = getEmbed(url);
    if (!embed) return;

    document.body.classList.add('is-modal-open');
    getLenis()?.stop();

    frame.innerHTML = renderFrame(embed);
    modal.classList.remove('is-loaded');
    const media = frame.querySelector('iframe, video');
    if (media) {
      media.addEventListener('load', () => modal.classList.add('is-loaded'), { once: true });
      // Safety: assume ready after 1.5s regardless.
      window.setTimeout(() => modal.classList.add('is-loaded'), 1500);
    } else {
      modal.classList.add('is-loaded');
    }

    if (typeof modal.showModal === 'function') {
      modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }
  };

  const close = (): void => {
    // Stop playback by ripping the iframe out — pausing isn't reliable
    // cross-origin.
    frame.innerHTML = '';
    document.body.classList.remove('is-modal-open');
    getLenis()?.start();
    if (modal.open && typeof modal.close === 'function') {
      modal.close();
    } else {
      modal.removeAttribute('open');
    }
  };

  // Open triggers: any element carrying data-video-url.
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest<HTMLElement>('[data-video-url]');
    if (!trigger) return;
    const url = trigger.dataset.videoUrl ?? '';
    if (!url || url === '#') return;
    event.preventDefault();
    open(url);
  });

  // Close triggers.
  closeBtn.addEventListener('click', close);
  modal.addEventListener('cancel', (e) => { // ESC key on <dialog>
    e.preventDefault();
    close();
  });
  // Click on backdrop (anywhere on the <dialog> itself that isn't a child).
  modal.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });
}
